import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EntityCombobox from "@/components/EntityCombobox";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  CURRENCY,
  type GlAccount,
  type GlLedgerResponse,
  todayIso,
} from "@/lib/gl";

function fmt(n: number) {
  if (Math.abs(n) < 0.005) return "—";
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function accountLabel(a: GlAccount) {
  return `${a.code} — ${a.name}`;
}

export default function GeneralLedgerLedger() {
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [accountId, setAccountId] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState<GlLedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Paginate display only — running balances are precomputed server-side over
  // the full result set, so slicing rows never changes their values.
  const pg = usePagination(data?.lines ?? []);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    (async () => {
      try {
        const a = await api.get<{ accounts: GlAccount[] }>(
          "/gl/accounts?is_active=1",
        );
        setAccounts(a.accounts);
      } catch (err) {
        toast.error((err as Error).message || "Failed to load accounts");
      }
    })();
  }, []);

  const run = async () => {
    if (!accountId) {
      toast.error("Pick an account");
      return;
    }
    setLoading(true);
    try {
      const qs = new URLSearchParams({ account_id: accountId });
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const d = await api.get<GlLedgerResponse>(
        `/gl/reports/general-ledger?${qs.toString()}`,
      );
      setData(d);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Date", "Reference", "Memo", "Debit", "Credit", "Running"],
      ...data.lines.map((l) => [
        l.entry_date,
        l.reference,
        l.description || l.journal_description || "",
        l.debit.toFixed(2),
        l.credit.toFixed(2),
        l.running_balance.toFixed(2),
      ]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((v) => {
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ledger-${data.account.code}-${from || "start"}-${to || "end"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-lg">Per-Account Ledger</CardTitle>
          <CardDescription>
            All posted entries hitting a single account, with a running balance.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <Label htmlFor="acc">Account</Label>
              <EntityCombobox
                id="acc"
                aria-label="Account"
                placeholder="Select account"
                value={(() => {
                  const idNum = Number(accountId);
                  if (!idNum) return "";
                  const a = accounts.find((x) => x.id === idNum);
                  return a ? accountLabel(a) : "";
                })()}
                options={accounts.map((a) => ({
                  id: a.id,
                  name: accountLabel(a),
                  secondary: a.type,
                }))}
                emptyText="No accounts available."
                onSelect={(opt) => setAccountId(String(opt.id))}
                onClear={() => setAccountId("")}
              />
            </div>
            <div>
              <Label htmlFor="from">From</Label>
              <Input
                id="from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="to">To</Label>
              <Input
                id="to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={run} disabled={loading || !accountId}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Run
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!data}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg">
              <span className="font-mono mr-2">{data.account.code}</span>
              {data.account.name}
            </CardTitle>
            <CardDescription>
              Opening: {fmt(data.opening_balance)} · Closing:{" "}
              <span className="font-semibold">{fmt(data.closing_balance)}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Memo</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Running</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      No posted activity in this range.
                    </TableCell>
                  </TableRow>
                ) : (
                  pg.pageItems.map((l, i) => (
                    <TableRow key={`${l.journal_id}-${l.line_no}-${i}`}>
                      <TableCell>{l.entry_date}</TableCell>
                      <TableCell>
                        <Link
                          to={`/general-ledger/journals/${l.journal_id}`}
                          className="text-primary hover:underline font-mono"
                        >
                          {l.reference}
                        </Link>
                      </TableCell>
                      <TableCell className="max-w-[40ch] truncate">
                        {l.description || l.journal_description || "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {l.debit > 0 ? fmt(l.debit) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {l.credit > 0 ? fmt(l.credit) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {fmt(l.running_balance)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {data.lines.length > 0 ? (
              <Pagination
                page={pg.page}
                pageCount={pg.pageCount}
                total={pg.total}
                from={pg.from}
                to={pg.to}
                onPrev={pg.prev}
                onNext={pg.next}
              />
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
